import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import UserAccessNav from '../components/UserAccessNav';
import PaginationControls from '../components/PaginationControls';
import AccessEventsTable from '../components/AccessEventsTable';
import { UserContext } from '../App';
import {
  deleteUserRegistry,
  getUserRegistry,
  listUserAccess,
  PagedResponse,
  UserAccessEvent,
  UserRegistryRecord,
} from '../util/userAccessApi';

const PAGE_SIZE = 25;

function UserAccessUserDetail() {
  const userCtx = useContext(UserContext);
  const navigate = useNavigate();
  const params = useParams();
  const userId = Number(params.userId);

  const [page, setPage] = useState(1);
  const [user, setUser] = useState<UserRegistryRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessData, setAccessData] = useState<PagedResponse<UserAccessEvent> | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!userCtx.user || Number.isNaN(userId)) {
      return;
    }

    setLoading(true);
    setError(null);

    getUserRegistry(userId)
      .then((loadedUser) => {
        setUser(loadedUser);
        return listUserAccess(page, PAGE_SIZE, loadedUser.userid);
      })
      .then((response) => setAccessData(response))
      .catch((e: Error) => {
        console.error('Failed to load user detail!', e);
        setError(e.message || 'Nepodarilo sa načítať používateľa.');
      })
      .finally(() => setLoading(false));
  }, [page, userCtx.user, userId]);

  const handleDelete = () => {
    if (!user || deleting) {
      return;
    }
    if (!window.confirm(`Naozaj chcete vymazať používateľa ${user.name}?`)) {
      return;
    }

    setDeleting(true);
    setError(null);

    deleteUserRegistry(user.id)
      .then(() => navigate('/userAccessUsers'))
      .catch((e: Error) => {
        console.error('Failed to delete user!', e);
        setError(e.message || 'Nepodarilo sa vymazať používateľa.');
      })
      .finally(() => setDeleting(false));
  };

  return (
    <div className="flex flex-col min-h-0 h-full">
      <Header />
      <UserAccessNav />

      <div className="flex-1 overflow-auto p-6">
        {!userCtx.user && <div>Neprihlásený!</div>}
        {userCtx.user && loading && <div>Načítavam...</div>}
        {userCtx.user && error && <div className="text-red-600">{error}</div>}
        {userCtx.user && user && !loading && !error && (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold">{user.name}</h1>
                <div className="mt-2 text-lg text-gray-700">User ID: {user.userid}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link className="btn-secondary" to="/userAccessUsers">
                  Späť na zoznam
                </Link>
                <Link className="btn-primary" to={`/userAccessUsers/${user.id}/edit`}>
                  Upraviť
                </Link>
                <button className="btn-danger" type="button" disabled={deleting} onClick={handleDelete}>
                  {deleting ? 'Mažem...' : 'Vymazať'}
                </button>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="text-xl font-bold">Prístupy používateľa</h2>
              {accessData && (
                <>
                  <div className="mt-4">
                    Celkom záznamov: <strong>{accessData.totalItems}</strong>
                  </div>
                  <AccessEventsTable events={accessData.items} emptyMessage="Používateľ zatiaľ nemá žiadne prístupy." />
                  <PaginationControls page={accessData.page} totalPages={accessData.totalPages} onChange={setPage} />
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default UserAccessUserDetail;
